import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { getToken } from '../utils/auth';
import { MembersAPI } from '../utils/api';
import { toast } from 'react-hot-toast';
import {
    FaUser, FaEnvelope, FaPhone, FaBirthdayCake, FaVenusMars,
    FaSearch, FaFilter, FaUsers, FaSpinner, FaCrown, FaStar, FaUserGraduate
} from 'react-icons/fa';
import SpinnerLoading from '../components/SpinnerLoading';

interface Member {
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'lop_truong' | 'bi_thu' | 'pho_bi_thu' | 'lop_pho_hoc_tap' | 'lop_pho_doi_song' | 'thu_quy' | 'uy_vien' | 'thanh_vien' | 'user';
    avatar?: string;
    phone?: string;
    dob?: string;
    gender?: 'male' | 'female' | 'other';
    created_at: string;
    last_login_at?: string;
}

const ROLE_NAMES: Record<string, string> = {
    'admin': 'Quản Trị Viên',
    'lop_truong': 'Lớp Trưởng',
    'bi_thu': 'Bí Thư',
    'pho_bi_thu': 'Phó Bí Thư',
    'lop_pho_hoc_tap': 'Lớp Phó Học Tập',
    'lop_pho_doi_song': 'Lớp Phó Đời Sống',
    'thu_quy': 'Thủ Quỹ',
    'uy_vien': 'Ủy Viên',
    'thanh_vien': 'Thành Viên',
    'user': 'Thành Viên'
};

const BCS_ROLES = ['lop_truong', 'bi_thu', 'pho_bi_thu', 'lop_pho_hoc_tap', 'lop_pho_doi_song', 'thu_quy', 'uy_vien'];

const MembersPage: React.FC = () => {
    const { isDarkMode } = useTheme();
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGroup, setFilterGroup] = useState<'all' | 'admin' | 'bcs' | 'member'>('all');
    const [activePhonePopup, setActivePhonePopup] = useState<string | null>(null);

    useEffect(() => {
        const handleClickOutside = () => setActivePhonePopup(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        loadMembers();
    }, []);

    const loadMembers = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;

            const res = await MembersAPI.listPublic(token);
            if (res && res.members) {
                setMembers(res.members);
            }
        } catch (error) {
            console.error('Failed to load members', error);
            toast.error('Không thể tải danh sách thành viên');
        } finally {
            setLoading(false);
        }
    };

    const getMemberGroup = (role: string) => {
        if (role === 'admin') return 'admin';
        if (BCS_ROLES.includes(role)) return 'bcs';
        return 'member';
    };

    const filteredMembers = members.filter(member => {
        const matchesSearch =
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (member.phone && member.phone.includes(searchTerm));

        const group = getMemberGroup(member.role);
        const matchesGroup = filterGroup === 'all' || group === filterGroup;

        return matchesSearch && matchesGroup;
    });

    const groupedMembers = {
        admin: filteredMembers.filter(m => getMemberGroup(m.role) === 'admin'),
        bcs: filteredMembers.filter(m => getMemberGroup(m.role) === 'bcs'),
        member: filteredMembers.filter(m => getMemberGroup(m.role) === 'member')
    };

    const formatGender = (gender?: string) => {
        switch (gender) {
            case 'male': return 'Nam';
            case 'female': return 'Nữ';
            default: return 'Khác';
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Chưa cập nhật';
        return new Date(dateString).toLocaleDateString('vi-VN');
    };

    const ZaloIcon = ({ className }: { className?: string }) => (
        <svg
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <title>Zalo</title>
            <path d="M12.49 10.2722v-.4496h1.3467v6.3218h-.7704a.576.576 0 01-.5763-.5729l-.0006.0005a3.273 3.273 0 01-1.9372.6321c-1.8138 0-3.2844-1.4697-3.2844-3.2823 0-1.8125 1.4706-3.2822 3.2844-3.2822a3.273 3.273 0 011.9372.6321l.0006.0005zM6.9188 7.7896v.205c0 .3823-.051.6944-.2995 1.0605l-.03.0343c-.0542.0615-.1815.206-.2421.2843L2.024 14.8h4.8948v.7682a.5764.5764 0 01-.5767.5761H0v-.3622c0-.4436.1102-.6414.2495-.8476L4.8582 9.23H.1922V7.7896h6.7266zm8.5513 8.3548a.4805.4805 0 01-.4803-.4798v-7.875h1.4416v8.3548H15.47zM20.6934 9.6C22.52 9.6 24 11.0807 24 12.9044c0 1.8252-1.4801 3.306-3.3066 3.306-1.8264 0-3.3066-1.4808-3.3066-3.306 0-1.8237 1.4802-3.3044 3.3066-3.3044zm-10.1412 5.253c1.0675 0 1.9324-.8645 1.9324-1.9312 0-1.065-.865-1.9295-1.9324-1.9295s-1.9324.8644-1.9324 1.9295c0 1.0667.865 1.9312 1.9324 1.9312zm10.1412-.0033c1.0737 0 1.945-.8707 1.945-1.9453 0-1.073-.8713-1.9436-1.945-1.9436-1.0753 0-1.945.8706-1.945 1.9436 0 1.0746.8697 1.9453 1.945 1.9453z" fill="#0068FF" />
        </svg>
    );

    const renderPhoneWithPopup = (member: Member) => {
        if (!member.phone) {
            return <span className="text-gray-400 italic">---</span>;
        }

        const isActive = activePhonePopup === member.id;

        return (
            <div className="relative inline-block" onClick={e => e.stopPropagation()}>
                <button
                    onClick={() => setActivePhonePopup(isActive ? null : member.id)}
                    className="hover:text-blue-500 hover:underline transition-colors focus:outline-none font-medium flex items-center gap-1"
                >
                    {member.phone}
                </button>

                {isActive && (
                    <div className="absolute left-0 bottom-full mb-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-600 z-50 overflow-hidden animate-fadeIn">
                        <a
                            href={`tel:${member.phone}`}
                            className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                            <FaPhone className="text-xs text-green-500" />
                            Gọi điện
                        </a>
                        <a
                            href={`https://zalo.me/${member.phone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 border-t border-gray-100 dark:border-gray-700"
                            onClick={() => setActivePhonePopup(null)}
                        >
                            <ZaloIcon className="w-4 h-4" />
                            Chat Zalo
                        </a>
                    </div>
                )}
            </div>
        );
    };

    const renderMemberList = (list: Member[], title: string, icon: React.ReactNode, type: 'admin' | 'bcs' | 'member') => {
        if (list.length === 0) return null;

        return (
            <div className="mb-8 last:mb-0">
                <div className="flex items-center gap-3 mb-4 px-2">
                    <div className={`p-2 rounded-lg ${type === 'admin' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                        type === 'bcs' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                            'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                        {icon}
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white uppercase tracking-wide">
                        {title} <span className="text-sm font-normal text-gray-500 dark:text-gray-400 normal-case">({list.length})</span>
                    </h2>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Mobile View */}
                    <div className="block md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                        {list.map((member) => (
                            <div key={member.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className="relative w-12 h-12 rounded-full overflow-hidden border border-gray-200 dark:border-gray-600 flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                        {member.avatar ? (
                                            <img
                                                src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_API_URL?.replace('/api', '')}${member.avatar}`}
                                                alt={member.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-lg font-bold text-gray-500 uppercase">
                                                {member.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <h3 className="font-semibold text-gray-900 dark:text-white truncate pr-2">
                                                {member.name}
                                            </h3>
                                            <span className={`
                                                flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap
                                                ${type === 'admin' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                                    type === 'bcs' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                                                        'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}
                                            `}>
                                                {ROLE_NAMES[member.role] || 'Thành Viên'}
                                            </span>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                <FaEnvelope className="text-xs text-blue-500 flex-shrink-0" />
                                                <span className="truncate break-all">{member.email}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                    <FaPhone className="text-xs text-green-500 flex-shrink-0" />
                                                    {renderPhoneWithPopup(member)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Thành viên</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Liên hệ</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Thông tin</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Vai trò</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {list.map((member) => (
                                    <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="relative w-10 h-10 rounded-full overflow-hidden border border-gray-200 dark:border-gray-600 flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                                    {member.avatar ? (
                                                        <img
                                                            src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_API_URL?.replace('/api', '')}${member.avatar}`}
                                                            alt={member.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-500 uppercase">
                                                            {member.name.charAt(0)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="font-semibold text-gray-900 dark:text-white">
                                                    {member.name}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                    <FaEnvelope className="text-xs text-blue-500 w-4" />
                                                    <span className="truncate max-w-[200px]" title={member.email}>{member.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                    <FaPhone className="text-xs text-green-500 w-4" />
                                                    {renderPhoneWithPopup(member)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                    <FaBirthdayCake className="text-xs text-pink-500 w-4" />
                                                    <span className={!member.dob ? 'text-gray-400 italic' : ''}>
                                                        {formatDate(member.dob) === 'Chưa cập nhật' ? '---' : formatDate(member.dob)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                                    <FaVenusMars className="text-xs text-purple-500 w-4" />
                                                    <span className={!member.gender ? 'text-gray-400 italic' : ''}>{formatGender(member.gender)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 w-40 text-center">
                                            <span className={`
                                                px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide inline-block text-center min-w-[100px] whitespace-nowrap
                                                ${type === 'admin' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                                    type === 'bcs' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                                                        'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}
                                            `}>
                                                {ROLE_NAMES[member.role] || 'Thành Viên'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
                <div style={{ transform: 'scale(0.435)' }}>
                    <SpinnerLoading />
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Hero Section */}
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>
                <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-transparent via-white/40 to-transparent blur-[2px] animate-[shimmer_3s_ease-in-out_infinite] [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)] mix-blend-overlay pointer-events-none"></div>

                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">
                            Thành viên Lớp
                        </h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">
                            Danh sách {members.length} thành viên trong hệ thống
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fadeIn">
                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-8 flex flex-col md:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full md:w-96">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Tìm kiếm theo tên, email, sđt..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                            <button
                                onClick={() => setFilterGroup('all')}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${filterGroup === 'all'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Tất cả
                            </button>
                            <button
                                onClick={() => setFilterGroup('admin')}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${filterGroup === 'admin'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Admin
                            </button>
                            <button
                                onClick={() => setFilterGroup('bcs')}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${filterGroup === 'bcs'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Ban Cán Sự
                            </button>
                            <button
                                onClick={() => setFilterGroup('member')}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${filterGroup === 'member'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Thành Viên
                            </button>
                        </div>
                    </div>
                </div>

                {/* Role Groups */}
                {filteredMembers.length > 0 ? (
                    <div className="space-y-8">
                        {renderMemberList(groupedMembers.admin, 'Quản Trị Viên', <FaCrown />, 'admin')}
                        {renderMemberList(groupedMembers.bcs, 'Ban Cán Sự Lớp', <FaStar />, 'bcs')}
                        {renderMemberList(groupedMembers.member, 'Thành Viên Lớp', <FaUserGraduate />, 'member')}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
                        <FaUsers className="mx-auto text-3xl mb-3 opacity-50" />
                        <p>Không tìm thấy thành viên nào phù hợp.</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default MembersPage;
